import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type SubscriptionStatus = {
  active: boolean;
  plan: string;
  /** Raw Stripe subscription status: 'trialing' | 'active' | 'past_due' | ... */
  stripeStatus: string;
  current_period_end: string | null;
  /** ISO date when the trial ends. Only set while `stripeStatus === 'trialing'`. */
  trial_end: string | null;
  cancel_at_period_end: boolean;
  /**
   * When access comes from a coupon redemption or admin grant (i.e. there
   * is no active Stripe subscription), this surfaces *why*. `null` means
   * the user is on Stripe-billed Pro or is on the free tier.
   * Possible values mirror the `coupon_grant_source` column on
   * `subscribers` — typically "coupon" or "admin".
   */
  grant_source: string | null;
};

export const useSubscription = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus(null);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("check-subscription");
    setLoading(false);
    if (error) {
      console.error("check-subscription failed", error);
      return;
    }
    setStatus({
      active: !!data?.active,
      plan: data?.plan ?? "free",
      stripeStatus: data?.status ?? "inactive",
      current_period_end: data?.current_period_end
        ? new Date(data.current_period_end * 1000).toISOString()
        : null,
      trial_end: data?.trial_end
        ? new Date(data.trial_end * 1000).toISOString()
        : null,
      cancel_at_period_end: !!data?.cancel_at_period_end,
      grant_source: data?.grant_source ?? null,
    });
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startCheckout = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("create-checkout");
    if (error) {
      console.error("create-checkout failed", error);
      return null;
    }
    if (data?.url) {
      window.location.href = data.url;
      return data.url as string;
    }
    return null;
  }, []);

  return { status, loading, refresh, startCheckout };
};
